import { FC, useEffect } from 'react';

import {
  ContactDetailsFormFields,
  LeadContactDetailsShell,
  createSalesforceLeadContact,
  useContactDetailsForm,
} from '@eneco/flows/components';
import { useStepContent } from '@eneco/flows/hooks';
import { useFlowHooks } from '@eneco/flows/utils/FlowProvider';
import { DynamicLeadPayload } from '@monorepo-types/dc/sales';
import { useMachine } from '@xstate/react';

import { useFlowTracking } from '../../hooks/useFlowTracking';
import { leadsMachine } from '../../machines/leadsMachine';
import { SolarBoilerLeadFlowContext } from '../../types/context';

interface FormFields {
  phoneNumber: string;
  emailAddress?: string;
  C1?: boolean;
}

export const ContactDetailsStep: FC = () => {
  const { useFlowSelector, useFlowActorRef } = useFlowHooks<SolarBoilerLeadFlowContext>();
  const phoneNumber = useFlowSelector(state => state.context.phoneNumber);
  const firstName = useFlowSelector(state => state.context.firstName);
  const emailAddress = useFlowSelector(state => state.context.emailAddress);
  const surnamePreposition = useFlowSelector(state => state.context.surnamePreposition);
  const salutation = useFlowSelector(state => state.context.salutation);
  const postalCode = useFlowSelector(state => state.context.postalCode);
  const surname = useFlowSelector(state => state.context.surname);
  const houseNumber = useFlowSelector(state => state.context.houseNumber);
  const houseNumberSuffix = useFlowSelector(state => state.context.houseNumberSuffix);
  const C1 = useFlowSelector(state => state.context.C1) ?? true;
  const [leadsState, sendToLeadsMachine] = useMachine(leadsMachine);
  const sendToFlowMachine = useFlowActorRef().send;
  const { trackPurchase } = useFlowTracking();
  const content = useStepContent('SolarBoilerLeadFlowContactDetailsStep');

  const {
    handleSubmit,
    getValues,
    register,
    formState: { errors, isSubmitSuccessful },
  } = useContactDetailsForm<FormFields>(undefined, { phoneNumber, emailAddress, C1 }, content);

  useEffect(() => {
    if (leadsState.matches('SUCCESS')) {
      trackPurchase();
      sendToFlowMachine({
        type: 'NEXT_PAGE',
        values: { ...getValues() },
      });
    }
  }, [leadsState, getValues, sendToFlowMachine, trackPurchase]);

  useEffect(() => {
    const formFieldsValues = getValues();
    if (isSubmitSuccessful) {
      sendToLeadsMachine({
        type: 'SEND_ORDER',
        values: {
          requestBody: {
            data: {
              ...createSalesforceLeadContact({
                firstName,
                surnamePreposition,
                salutation,
                optIn: formFieldsValues.C1,
                surname,
                postalCode,
                phoneNumber: formFieldsValues.phoneNumber,
                emailAddress: formFieldsValues.emailAddress,
                houseNumber,
                houseNumberSuffix,
              }),
              Interest_Solarpower_Boiler__c: 'true',
            },
          } as DynamicLeadPayload,
        },
      });
    }
  }, [isSubmitSuccessful]);

  return (
    <LeadContactDetailsShell
      content={content}
      handleSubmit={handleSubmit}
      hasError={leadsState.matches('ERROR')}
      isLoading={isSubmitSuccessful && !leadsState.matches('ERROR')}>
      <ContactDetailsFormFields register={register} errors={errors} content={content} />
    </LeadContactDetailsShell>
  );
};
